// src/components/data-display/BitcoinCalculatorAdvanced.jsx


import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useBreakpointValue } from '@chakra-ui/react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightElement,
  Select,
  FormControl,
  FormLabel,
  useColorMode,
  Switch,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  Box,
  IconButton,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBitcoinSign, faWallet, faCalculator, faTimes, faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { useRecoilState } from 'recoil';
import { selectedCurrencyState } from '../../recoilState';
import { createCurrencyConverter, currencySymbols } from '../../utils/currencyConversion.utils';

const formatFiatAmount = (amount) => {
  return isNaN(amount) ? '0' : Math.round(amount).toLocaleString();
};

const formatSatsAmount = (amount) => {
  return isNaN(amount) ? '0' : parseInt(amount).toLocaleString();
};

const convertCryptoToAmount = (cryptoAmount, bitcoinPrice, conversionRate) => {
  const sanitizedValue = parseFloat(cryptoAmount.replace(/,/g, ''));
  if (!isNaN(sanitizedValue) && !isNaN(bitcoinPrice) && !isNaN(conversionRate)) {
    const convertedAmount = Math.round(sanitizedValue * bitcoinPrice * conversionRate);
    return formatFiatAmount(convertedAmount);
  } else {
    return '0';
  }
};

const formatLargeNumber = (num) => {
  if (typeof num !== 'number' || isNaN(num)) {
    return '0.00';
  }
  const absNum = Math.abs(num);
  if (absNum >= 1e15) return (num / 1e15).toFixed(2) + 'Qu';
  if (absNum >= 1e12) return (num / 1e12).toFixed(2) + 'T';
  if (absNum >= 1e9) return (num / 1e9).toFixed(2) + 'B';
  if (absNum >= 1e6) return (num / 1e6).toFixed(2) + 'M';
  if (absNum >= 1e3) return (num / 1e3).toFixed(2) + 'K';
  return num.toFixed(2);
};

export const BitcoinCalculatorAdvanced = ({ isOpen, onClose, data, bitcoinPrice, formatNumber }) => {
  const [isCalculatorView, setIsCalculatorView] = useState(true);
  const breakpointValue = useBreakpointValue({ base: 'column', md: 'column' });
  const gapValue = useBreakpointValue({ base: 4, md: 4 });
  const [cryptoAmount, setCryptoAmount] = useState('1');
  const [satsAmount, setSatsAmount] = useState('100,000,000');
  const [cryptoCurrency, setCryptoCurrency] = useState('BTC');
  const [fiatAmount, setFiatAmount] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useRecoilState(selectedCurrencyState);
  const [conversionRates, setConversionRates] = useState({});
  const [portfolio, setPortfolio] = useState([]);
  const [newEntry, setNewEntry] = useState({
    btcAmount: '',
    purchasePrice: '',
    purchaseDate: '',
  });
  const [editingEntry, setEditingEntry] = useState(null);
  const prevCurrencyRef = useRef(selectedCurrency);

  const { colorMode } = useColorMode();
  const bgColor = colorMode === 'dark' ? '#121212' : '#ffffff';
  const borderColor = colorMode === 'dark' ? '#ffffff' : '#121212';

  const isMobile = useBreakpointValue({ base: true, md: false });

  const [currencyChanged, setCurrencyChanged] = useState(false);

const shouldShowWarning = useCallback(() => {
  return currencyChanged && portfolio.length > 0;
}, [currencyChanged, portfolio.length]);

useEffect(() => {
  if (data) {
    const newConversionRates = createCurrencyConverter(data);
    setConversionRates(newConversionRates);
  }
  
  // Check if the currency has changed
  if (prevCurrencyRef.current !== selectedCurrency) {
    console.log('Currency changed from', prevCurrencyRef.current, 'to', selectedCurrency);
    prevCurrencyRef.current = selectedCurrency;
    setCurrencyChanged(true);
  }
}, [data, selectedCurrency]);

useEffect(() => {
  setCurrencyChanged(false);
}, [portfolio]);

  useEffect(() => {
    if (bitcoinPrice && conversionRates[selectedCurrency]) {
      setFiatAmount(convertCryptoToAmount(cryptoAmount, bitcoinPrice, conversionRates[selectedCurrency]));
    }
  }, [bitcoinPrice, selectedCurrency, cryptoAmount, conversionRates]);

  const handleCryptoAmountChange = useCallback((e) => {
    const value = e.target.value;
    setCryptoAmount(value);

    const satsValue = Math.floor(parseFloat(value) * 100000000);
    setSatsAmount(formatSatsAmount(satsValue));
    if (bitcoinPrice && conversionRates[selectedCurrency]) {
      setFiatAmount(convertCryptoToAmount(value, bitcoinPrice, conversionRates[selectedCurrency]));
    }
  }, [bitcoinPrice, conversionRates, selectedCurrency]);

  const handleSatsAmountChange = useCallback((e) => {
    const value = e.target.value.replace(/,/g, '');
    const numericValue = parseInt(value, 10);

    if (!isNaN(numericValue)) {
      setSatsAmount(formatSatsAmount(numericValue));

      const btcValue = (numericValue / 100000000).toFixed(8);
      setCryptoAmount(btcValue);
      if (bitcoinPrice && conversionRates[selectedCurrency]) {
        setFiatAmount(convertCryptoToAmount(btcValue, bitcoinPrice, conversionRates[selectedCurrency]));
      }
    } else {
      setSatsAmount('0');
      setCryptoAmount('0');
      setFiatAmount('0');
    }
  }, [bitcoinPrice, conversionRates, selectedCurrency]);

  const handleFiatAmountChange = useCallback((e) => {
    const value = e.target.value.replace(/,/g, '');
    const numericValue = parseFloat(value);

    if (!isNaN(numericValue) && bitcoinPrice && conversionRates[selectedCurrency]) {
      const formattedValue = numericValue.toLocaleString();
      setFiatAmount(formattedValue);

      const convertedBTCAmount = (numericValue / (bitcoinPrice * conversionRates[selectedCurrency])).toFixed(8);
      setCryptoAmount(convertedBTCAmount);

      const satsValue = Math.floor(parseFloat(convertedBTCAmount) * 100000000);
      setSatsAmount(formatSatsAmount(satsValue));
    } else {
      setFiatAmount('0');
      setCryptoAmount('0');
      setSatsAmount('0');
    }
  }, [bitcoinPrice, conversionRates, selectedCurrency]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setNewEntry(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleEditClick = useCallback((entry) => {
    setEditingEntry(entry);
    setNewEntry({
      btcAmount: entry.btcAmount,
      purchasePrice: entry.purchasePrice,
      purchaseDate: entry.purchaseDate,
    });
  }, []);

  const clearFields = useCallback(() => {
    setNewEntry({ btcAmount: '', purchasePrice: '', purchaseDate: '' });
  }, []);

  const addOrUpdateEntry = useCallback(() => {
    if (newEntry.btcAmount && newEntry.purchasePrice && newEntry.purchaseDate) {
      const entry = {
        ...newEntry,
        btcAmount: parseFloat(newEntry.btcAmount) || 0,
        purchasePrice: parseFloat(newEntry.purchasePrice) || 0,
        id: editingEntry ? editingEntry.id : Date.now()
      };

      setPortfolio(prevPortfolio => {
        if (editingEntry) {
          return prevPortfolio.map(item => item.id === editingEntry.id ? entry : item);
        } else {
          return [...prevPortfolio, entry];
        }
      });

      setEditingEntry(null);
      clearFields();
    }
  }, [newEntry, editingEntry, clearFields]);

  const cancelUpdate = useCallback(() => {
    setEditingEntry(null);
    clearFields();
  }, [clearFields]);

  const removeEntry = useCallback((id) => {
    setPortfolio(prevPortfolio => prevPortfolio.filter(entry => entry.id !== id));
  }, []);

  const calculateTotals = useCallback(() => {
    return portfolio.reduce((acc, entry) => {
      const currentValue = entry.btcAmount * (bitcoinPrice || 0) * conversionRates[selectedCurrency];
      const originalValue = entry.btcAmount * entry.purchasePrice;
      const profit = currentValue - originalValue;

      return {
        totalBtc: acc.totalBtc + (parseFloat(entry.btcAmount) || 0),
        totalValue: acc.totalValue + currentValue,
        totalCostBasis: acc.totalCostBasis + originalValue,
        totalProfit: acc.totalProfit + profit,
        totalOriginalOrderPrice: acc.totalOriginalOrderPrice + originalValue,
      };
    }, { totalBtc: 0, totalValue: 0, totalCostBasis: 0, totalProfit: 0, totalOriginalOrderPrice: 0 });
  }, [portfolio, bitcoinPrice, conversionRates, selectedCurrency]);

  const formatCurrency = useCallback((value) => {
    if (isNaN(value)) return '0.00';
    const formattedValue = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: selectedCurrency.toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
    return formatLargeNumber(parseFloat(formattedValue.replace(/[^0-9.-]+/g, '')));
  }, [selectedCurrency]);

  const calculateDaysHeld = useCallback((purchaseDate) => {
    const today = new Date();
    const purchase = new Date(purchaseDate);
    const diffTime = Math.abs(today - purchase);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }, []);

  const calculateProfitLossPercentage = (currentValue, originalValue) => {
    if (originalValue === 0) return 0;
    return ((currentValue - originalValue) / originalValue) * 100;
  };

  const renderMobileEntry = useCallback((entry) => {
    const currentValue = entry.btcAmount * (bitcoinPrice || 0) * conversionRates[selectedCurrency];
    const originalValue = entry.btcAmount * entry.purchasePrice;
    const profit = currentValue - originalValue;
    const profitPercentage = calculateProfitLossPercentage(currentValue, originalValue);
    const daysHeld = calculateDaysHeld(entry.purchaseDate);

    return (
      <Box 
        key={entry.id} 
        p={{ base: 3, sm: 4 }} 
        borderWidth="1px" 
        borderRadius="lg" 
        mb={3}
        bg={colorMode === 'dark' ? 'gray.800' : 'gray.50'}
      >
        <VStack align="stretch" spacing={2}>
          <Flex justifyContent="space-between" alignItems="flex-start">
            <VStack align="flex-start" spacing={1} flex="1">
              <Text fontSize="sm" fontWeight="bold" color="gray.500">BTC Amount</Text>
              <Text fontSize="md">{formatLargeNumber(entry.btcAmount)}</Text>
            </VStack>
            <HStack spacing={2}>
              <IconButton
                icon={<FontAwesomeIcon icon={faPencilAlt} />}
                size="xs"
                variant="ghost"
                colorScheme='orange'
                onClick={() => handleEditClick(entry)}
                aria-label="Edit entry"
              />
              <IconButton
                icon={<FontAwesomeIcon icon={faTimes} />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => removeEntry(entry.id)}
                aria-label="Remove entry"
              />
            </HStack>
          </Flex>
          
          <Flex justifyContent="space-between" flexWrap="wrap" gap={2}>
            <VStack align="flex-start" spacing={0} minW="45%">
              <Text fontSize="xs" color="gray.500">Purchase Price</Text>
              <Text fontSize="sm">{formatCurrency(entry.purchasePrice)}</Text>
            </VStack>
            <VStack align="flex-start" spacing={0} minW="45%">
              <Text fontSize="xs" color="gray.500">Purchase Date</Text>
              <Text fontSize="sm">{entry.purchaseDate}</Text>
            </VStack>
          </Flex>
          
          <Flex justifyContent="space-between" flexWrap="wrap" gap={2}>
            <VStack align="flex-start" spacing={0} minW="45%">
              <Text fontSize="xs" color="gray.500">Cost Basis</Text>
              <Text fontSize="sm">{formatCurrency(originalValue)}</Text>
            </VStack>
            <VStack align="flex-start" spacing={0} minW="45%">
              <Text fontSize="xs" color="gray.500">Current Value</Text>
              <Text fontSize="sm">{formatCurrency(currentValue)}</Text>
            </VStack>
          </Flex>
          
          <Box 
            p={2} 
            borderRadius="md" 
            bg={profit >= 0 ? 'green.50' : 'red.50'}
            _dark={{ bg: profit >= 0 ? 'green.900' : 'red.900' }}
          >
            <Flex justifyContent="space-between" alignItems="center">
              <Text fontSize="sm" fontWeight="bold" color={profit >= 0 ? 'green.600' : 'red.600'} _dark={{ color: profit >= 0 ? 'green.200' : 'red.200' }}>
                P/L: {formatCurrency(profit)}
              </Text>
              <HStack spacing={2}>
                <Text fontSize="sm" color={profit >= 0 ? 'green.600' : 'red.600'} _dark={{ color: profit >= 0 ? 'green.200' : 'red.200' }}>
                  {formatNumber(profitPercentage, 2)}%
                </Text>
                <Text fontSize="xs" color="gray.500">
                  ({daysHeld}d)
                </Text>
              </HStack>
            </Flex>
          </Box>
        </VStack>
      </Box>
    );
  }, [bitcoinPrice, formatCurrency, handleEditClick, removeEntry, calculateDaysHeld, formatNumber, conversionRates, selectedCurrency, colorMode]);

  const totals = calculateTotals();

  if (!data || !bitcoinPrice) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay backgroundColor="rgba(16, 22, 26, 0.9)" />
        <ModalContent bg={bgColor} pb={6} borderWidth="2px" borderStyle="solid" borderColor={borderColor} borderRadius="6px" overflow="hidden">
          <ModalHeader>Loading...</ModalHeader>
          <ModalBody>
            <Text>Please wait while we fetch the necessary data.</Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }



  return (
    <Modal isOpen={isOpen} onClose={onClose} size={isCalculatorView ? "xl" : "6xl"}>
      <ModalOverlay backgroundColor="rgba(16, 22, 26, 0.9)" />
      <ModalContent bg={bgColor} pb={6} borderWidth="2px" borderStyle="solid" borderColor={borderColor} borderRadius="6px" overflow="hidden">
        <ModalHeader>
          <Flex align="center" justify="flex-start">
            <FontAwesomeIcon icon={isCalculatorView ? faCalculator : faWallet} style={{ marginRight: '10px' }} />
            <Text mr={4}>{isCalculatorView ? "BTC Calculator" : "Profit/Loss Calculator"}</Text>
            <Flex align="center">
              <Text fontSize="xs" mr={2}>{isCalculatorView ? "P/L Calculator" : "BTC Calculator"}</Text>
              <Switch
                size={'sm'}
                colorScheme='gray'
                isChecked={!isCalculatorView}
                onChange={() => setIsCalculatorView(!isCalculatorView)}
              />
            </Flex>
          </Flex>
        </ModalHeader>
        <ModalCloseButton
          _hover={{
            bg: 'gray.500',
          }}
          _focus={{
            boxShadow: '0 0 0 1px rgba(66, 153, 225, 0.6)',
          }}
        />
        <ModalBody>
          {isCalculatorView ? (
            <Flex width="100%" direction={breakpointValue} alignItems="flex-end" gap={gapValue}>
              <FormControl>
                <FormLabel>Bitcoin</FormLabel>
                <InputGroup>
                  <InputLeftAddon><FontAwesomeIcon icon={faBitcoinSign} /></InputLeftAddon>
                  <Input type="number" value={cryptoAmount} onChange={handleCryptoAmountChange} borderWidth="1.5px" fontSize={'16px'} />
                  <InputRightElement width="auto">
                    <Select value={cryptoCurrency} onChange={(e) => setCryptoCurrency(e.target.value)} icon={'none'}>
                      <option value="BTC">BTC</option>
                    </Select>
                  </InputRightElement>
                </InputGroup>
              </FormControl>
  
              <FormControl>
                <FormLabel>Sats</FormLabel>
                <InputGroup>
                  <InputLeftAddon fontSize={16}>ṩ</InputLeftAddon>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{1,3}(,\d{3})*"
                    value={satsAmount}
                    onChange={handleSatsAmountChange}
                    borderWidth="1.5px"
                    fontSize={'16px'}
                  />
                  <InputRightElement width="auto">
                    <Select value={cryptoCurrency} onChange={(e) => setCryptoCurrency(e.target.value)} icon={'none'}>
                      <option value="Sats">Sats</option>
                    </Select>
                  </InputRightElement>
                </InputGroup>
              </FormControl>
  
              <FormControl ml={[0, 4]} mt={[4, 0]}>
                <FormLabel>Conversion</FormLabel>
                <InputGroup>
                  <InputLeftAddon>{currencySymbols[selectedCurrency] || '$'}</InputLeftAddon>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{1,3}(,\d{3})*"
                    value={fiatAmount}
                    onChange={handleFiatAmountChange}
                    borderWidth="1.5px"
                    fontSize={'16px'}
                  />
                  <InputRightElement width="auto" borderWidth="2px" borderRadius="6px" >
                    <Select value={selectedCurrency} onChange={(e) => setSelectedCurrency(e.target.value)}>
                      {Object.keys(currencySymbols).map((currency) => (
                        <option key={currency} value={currency}>{currency.toUpperCase()}</option>
                      ))}
                    </Select>
                  </InputRightElement>
                </InputGroup>
              </FormControl>
            </Flex>
          ) : (
            <>
              <Flex 
                direction={{ base: 'column', md: 'row' }} 
                gap={{ base: 3, md: 4 }} 
                mb={4}
                flexWrap={{ md: 'wrap', lg: 'nowrap' }}
              >
                <FormControl flex={{ base: '1', md: '1', lg: '1' }} minW={{ base: '100%', md: '45%', lg: 'auto' }}>
                  <FormLabel fontSize={{ base: 'sm', md: 'md' }}>BTC Amount</FormLabel>
                  <InputGroup size={{ base: 'md', md: 'md' }}>
                    <InputLeftAddon><FontAwesomeIcon icon={faBitcoinSign} /></InputLeftAddon>
                    <Input
                      type="number"
                      name="btcAmount"
                      value={newEntry.btcAmount}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      fontSize={{ base: '16px', md: '16px' }}
                    />
                  </InputGroup>
                </FormControl>
                <FormControl flex={{ base: '1', md: '1', lg: '1' }} minW={{ base: '100%', md: '45%', lg: 'auto' }}>
                  <FormLabel fontSize={{ base: 'sm', md: 'md' }}>Purchase Price ({selectedCurrency.toUpperCase()})</FormLabel>
                  <InputGroup size={{ base: 'md', md: 'md' }}>
                    <InputLeftAddon>{currencySymbols[selectedCurrency] || '$'}</InputLeftAddon>
                    <Input
                      type="number"
                      name="purchasePrice"
                      value={newEntry.purchasePrice}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      fontSize={{ base: '16px', md: '16px' }}
                    />
                  </InputGroup>
                </FormControl>
                <FormControl flex={{ base: '1', md: '1', lg: '1' }} minW={{ base: '100%', md: '45%', lg: 'auto' }}>
                  <FormLabel fontSize={{ base: 'sm', md: 'md' }}>Purchase Date</FormLabel>
                  <Input
                    type="date"
                    name="purchaseDate"
                    value={newEntry.purchaseDate}
                    onChange={handleInputChange}
                    size={{ base: 'md', md: 'md' }}
                    fontSize={{ base: '16px', md: '16px' }}
                  />
                </FormControl>
              </Flex>
              <Flex 
                justifyContent="center" 
                width="100%" 
                gap={{ base: 2, md: 4 }}
                flexWrap="wrap"
                mb={6}
              >
                <Button
                  onClick={addOrUpdateEntry}
                  size={{ base: "sm", md: "md" }}
                  colorScheme={editingEntry ? "green" : "orange"}
                  minW={{ base: "30%", md: "auto" }}
                  fontSize={{ base: "sm", md: "md" }}
                >
                  {editingEntry ? 'Update Entry' : 'Add Entry'}
                </Button>
  
                {editingEntry && (
                  <Button
                    onClick={cancelUpdate}
                    size={{ base: "sm", md: "md" }}
                    colorScheme="red"
                    minW={{ base: "30%", md: "auto" }}
                    fontSize={{ base: "sm", md: "md" }}
                  >
                    Cancel Update
                  </Button>
                )}
  
                <Button
                  onClick={clearFields}
                  size={{ base: "sm", md: "md" }}
                  colorScheme="gray"
                  minW={{ base: "20%", md: "auto" }}
                  fontSize={{ base: "sm", md: "md" }}
                >
                  Clear
                </Button>
              </Flex>
  
              {isMobile ? (
                <VStack mt={6} spacing={4} align="stretch">
                  {portfolio.map(renderMobileEntry)}
                </VStack>
              ) : (
                <Box overflowX="auto">
                  <Table variant="simple" mt={6} size="sm">
                    <Thead>
                      <Tr>
                        <Th>BTC Amount</Th>
                        <Th>Purchase Price</Th>
                        <Th>Purchase Date</Th>
                        <Th>Original Order Total</Th>
                        <Th>Current Value</Th>
                        <Th>Profit/Loss</Th>
                        <Th>P/L %</Th>
                        <Th>Days Held</Th>
                        <Th>Action</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {portfolio.map((entry) => {
                        const currentValue = entry.btcAmount * (bitcoinPrice || 0) * conversionRates[selectedCurrency];
                        const originalValue = entry.btcAmount * entry.purchasePrice;
                        const profit = currentValue - originalValue;
                        const profitPercentage = calculateProfitLossPercentage(currentValue, originalValue);
                        const daysHeld = calculateDaysHeld(entry.purchaseDate);
                        return (
                          <Tr key={entry.id}>
                            <Td>{formatLargeNumber(entry.btcAmount)}</Td>
                            <Td>{formatCurrency(entry.purchasePrice)}</Td>
                            <Td>{entry.purchaseDate}</Td>
                            <Td>{formatCurrency(originalValue)}</Td>
                            <Td>{formatCurrency(currentValue)}</Td>
                            <Td color={profit >= 0 ? 'green.500' : 'red.500'}>
                              {formatCurrency(profit)}
                            </Td>
                            <Td color={profit >= 0 ? 'green.500' : 'red.500'}>
                              {formatNumber(profitPercentage, 2)}%
                            </Td>
                            <Td>{formatNumber(daysHeld, 0)}</Td>
                            <Td>
                              <Flex>
                                <IconButton
                                  icon={<FontAwesomeIcon icon={faPencilAlt} />}
                                  size="xs"
                                  colorScheme="orange"
                                  mr={2}
                                  onClick={() => handleEditClick(entry)}
                                  aria-label="Edit entry"
                                />
                                <IconButton
                                  icon={<FontAwesomeIcon icon={faTimes} />}
                                  size="xs"
                                  colorScheme="red"
                                  onClick={() => removeEntry(entry.id)}
                                  aria-label="Remove entry"
                                />
                              </Flex>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              )}
  
              <Flex 
                direction={{ base: 'column', lg: 'row' }}
                justifyContent={{ base: 'flex-start', lg: 'space-between' }}
                alignItems={{ base: 'stretch', lg: 'flex-end' }}
                mt={6} 
                gap={{ base: 4, lg: 6 }}
              >
                <Box flex="1">
                  <Flex 
                    direction={{ base: 'column', sm: 'row' }}
                    gap={{ base: 4, sm: 6 }}
                    flexWrap="wrap"
                  >
                    <VStack align="flex-start" spacing={1} minW={{ base: '45%', md: 'auto' }}>
                      <Text fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>Stack Size</Text>
                      <Text fontSize={{ base: 'md', md: 'lg' }}>{formatLargeNumber(totals.totalBtc)} BTC</Text>
                    </VStack>
                    <VStack align="flex-start" spacing={1} minW={{ base: '45%', md: 'auto' }}>
                      <Text fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>Cost Basis</Text>
                      <Text fontSize={{ base: 'md', md: 'lg' }}>{formatCurrency(totals.totalOriginalOrderPrice)}</Text>
                    </VStack>
                    <VStack align="flex-start" spacing={1} minW={{ base: '45%', md: 'auto' }}>
                      <Text fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>Current Value</Text>
                      <Text fontSize={{ base: 'md', md: 'lg' }}>{formatCurrency(totals.totalValue)}</Text>
                    </VStack>
                    <VStack align="flex-start" spacing={1} minW={{ base: '45%', md: 'auto' }}>
                      <Text fontWeight="bold" fontSize={{ base: 'sm', md: 'md' }}>Profit/Loss</Text>
                      <Text 
                        color={totals.totalProfit >= 0 ? 'green.500' : 'red.500'}
                        fontSize={{ base: 'md', md: 'lg' }}
                      >
                        {formatCurrency(totals.totalProfit)} ({formatNumber(calculateProfitLossPercentage(totals.totalValue, totals.totalOriginalOrderPrice), 2)}%)
                      </Text>
                    </VStack>
                  </Flex>
                </Box>
                <VStack
                  align={{ base: 'flex-start', lg: 'flex-end' }}
                  spacing={1}
                  minW={{ base: '100%', lg: 'auto' }}
                  mt={{ base: 2, lg: 0 }}
                >
                  <Text
                    fontSize="xs"
                    color="gray.500"
                  >
                    For estimation or informational purposes only.
                  </Text>
                  {shouldShowWarning() && (
                    <Text
                      fontSize="xs"
                      color="red.500"
                      fontWeight="bold"
                    >
                      Currency changed. Some calculations may need to be updated.
                    </Text>
                  )}
                </VStack>
              </Flex>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default BitcoinCalculatorAdvanced;